import { createSelector } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { RootState } from '../store';
import { FsmActiveChapter } from './player.types';

export const selectIsPlayerPlaying = (state: RootState) =>
  state.player.isPlaying;

export const selectActiveChapterId = (state: RootState) =>
  state.player.activeChapter?.chapterId;

export const selectActiveEpisodeId = (state: RootState) =>
  state.player.activeChapter?.episodeId;

/** Only available for new Player Session FSM */
export const selectActiveSessionId = (state: RootState) =>
  (state.player.activeChapter as FsmActiveChapter)?.sessionId;

export const selectPlayerProgress = (state: RootState) => state.player.progress;

export const selectPlayerSeekToPosition = (state: RootState) =>
  state.player.seekToPosition;

export const selectIsSavingProgress = (state: RootState) =>
  state.player.isSavingProgress;

export const selectLastSavedProgress = (state: RootState) =>
  state.player.lastSavedProgress;

export const selectIsPlayerSeeking = (state: RootState) =>
  state.player.isSeeking;

export const selectPlayerAutoSaveInterval = (state: RootState) =>
  state.player.autoSaveInterval;

export const selectIsPlayerBuffering = (state: RootState) =>
  (state.player.isBuffering || !state.player.isTrackLoaded) &&
  state.player.isPlaying;

export const selectIsPlaying = (state: RootState) => state.player.isPlaying;

export const selectIsPlayerVideoFullscreen = (state: RootState) =>
  state.player.isVideoFullscreen;

export const selectIsPlayerExpanded = (state: RootState) =>
  state.player.isExpanded;

export const selectPlayerOnSeekMessage = (state: RootState) =>
  state.player.messages.onSeek;

export const selectPlayerLocationReferrer = (state: RootState) =>
  R.pick(['location', 'referrer'], state.player);

export const selectIsTrackLoaded = (state: RootState) =>
  state.player.isTrackLoaded;

export const selectIsChangingChapter = (state: RootState) =>
  state.player.isChangingChapter || state.player.isAttemptingToChangeChapter;

export const selectIsPlaybackRejected = (state: RootState) =>
  state.player.isPlaybackRejected;

export const selectPlaybackRejectedChapter = (state: RootState) =>
  state.player.playbackRejectedChapter;

export const selectPlaybackSpeedOptions = (state: RootState) =>
  state.player.playbackSpeedOptions;

export const selectIsEpisodePlaying = createSelector(
  [
    selectActiveEpisodeId,
    selectIsPlayerPlaying,
    (state, episodeId) => episodeId,
  ],
  (activeEpisodeId, isPlayerPlaying, episodeId) => {
    return activeEpisodeId === episodeId && isPlayerPlaying;
  },
);
